import cN from 'classnames';
import { startsWith } from 'lodash/fp';
import history, { getStateForBackgroundRoute, isPageReloadScheduled } from '../../../routing/history';
import { preventJavascriptUrls } from '../../../services/security';
import type { EventTrackingProps } from '../../services/Tracking';
import { trackEvent } from '../../services/Tracking';
import { prependAppPathToEmbbeddedWicketPages } from './service';
import styles from './style.m.less';

interface Props extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
    ref?: React.Ref<HTMLAnchorElement>;
    href: string;
    openInNewTab?: boolean;
    openInModal?: boolean;
    historyMethod?: 'push' | 'replace';
    hasTextStyle?: boolean;
    eventTracking?: EventTrackingProps;
    testId?: string;
}

function safeDefaultRel(href: string, target: '_blank' | string | undefined, rel: string | undefined) {
    if (!!rel || target !== '_blank') {
        return rel;
    }

    try {
        const url = new URL(href);
        const linkToMarketingWebsite =
            url.hostname === 'www.small-improvements.com' || url.hostname === 'small-improvements.com';

        return linkToMarketingWebsite ? 'noopener' : 'noopener noreferrer';
    } catch {
        return 'noopener noreferrer';
    }
}

export default function Link(props: Props) {
    const {
        href,
        title,
        children,
        className,
        style,
        target,
        rel,
        onClick,
        openInNewTab,
        openInModal,
        historyMethod = 'push',
        hasTextStyle = true,
        eventTracking,
        testId,
        ...passedProps
    } = props;

    const t = openInNewTab ? '_blank' : target;
    const r = safeDefaultRel(href, t, rel);

    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        const safeHref = preventJavascriptUrls(prependAppPathToEmbbeddedWicketPages(href));

        if (onClick) {
            onClick(e);
        }

        if (
            !history ||
            e.ctrlKey ||
            e.metaKey ||
            e.shiftKey ||
            e.button !== 0 ||
            openInNewTab ||
            isPageReloadScheduled()
        ) {
            return;
        }

        const isApp = startsWith('/app/', safeHref);

        if (!isApp || e.isDefaultPrevented()) {
            return;
        }

        e.preventDefault();

        history[historyMethod](safeHref.replace('/app', ''), openInModal ? getStateForBackgroundRoute() : undefined);
    };

    return (
        <a
            {...passedProps}
            href={preventJavascriptUrls(href)}
            className={cN(className, { [styles.reset]: !hasTextStyle })}
            style={style}
            title={title}
            onClick={e => {
                if (eventTracking) {
                    trackEvent(eventTracking);
                }
                handleClick(e);
            }}
            target={t}
            rel={r}
            data-testid={testId}
            data-react-link
        >
            {children}
        </a>
    );
}
